import { ArrowLeft, ArrowRight, ArrowUpRight, BellRing, Eye, Heart, Scale } from "lucide-react";
import { useRef, useState } from "react";

export default function VehicleCard({ vehicle, onDetails, onPreview, isCompared, onToggleCompare, compareDisabled, isFavorite, onToggleFavorite, change }) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const pointerStart = useRef(null);
  const images = vehicle.previewImages?.length ? vehicle.previewImages : [vehicle.image];
  const hasGallery = images.length > 1;

  const showImage = (index) => {
    setActiveImageIndex((index + images.length) % images.length);
  };

  const handlePointerMove = (event) => {
    if (!hasGallery || event.pointerType !== "mouse") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(0.999, (event.clientX - bounds.left) / bounds.width));
    showImage(Math.floor(relativeX * images.length));
  };

  const handlePointerDown = (event) => {
    if (!hasGallery || event.pointerType === "mouse") return;
    pointerStart.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerUp = (event) => {
    if (!hasGallery || pointerStart.current === null) return;
    const distance = event.clientX - pointerStart.current;
    if (Math.abs(distance) > 28) showImage(activeImageIndex + (distance < 0 ? 1 : -1));
    pointerStart.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleFrameKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      showImage(activeImageIndex + 1);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showImage(activeImageIndex - 1);
    }
  };

  return (
    <article className="vehicle-card" data-testid={`vehicle-card-${vehicle.id}`}>
      <div className="vehicle-image-frame" tabIndex={hasGallery ? 0 : -1} role={hasGallery ? "group" : undefined} aria-label={hasGallery ? "Fahrzeugfotos durchblättern" : undefined} onPointerMove={handlePointerMove} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={() => { pointerStart.current = null; }} onKeyDown={handleFrameKeyDown}>
        <img src={images[activeImageIndex]} alt={`${vehicle.brand} ${vehicle.title}, Ansicht ${activeImageIndex + 1}`} loading="lazy" />
        <span className="vehicle-badge">{vehicle.badge}</span>
        {change && <span className={`vehicle-change-badge ${change.type === "price" && change.direction === "down" ? "is-down" : ""}`} data-testid={`vehicle-change-${vehicle.id}`}><BellRing size={11} /> {change.type === "price" ? `Preis ${change.direction === "down" ? "gesenkt" : "geändert"} · vorher ${change.from}` : "Verfügbarkeit geändert"}</span>}
        {hasGallery && <>
          <span className="vehicle-gallery-hint">Wischen oder bewegen</span>
          <div className="vehicle-image-controls" aria-label="Fahrzeugansichten">
            <button type="button" aria-label="Vorherige Fahrzeugansicht" onClick={(event) => { event.stopPropagation(); showImage(activeImageIndex - 1); }} data-testid={`vehicle-image-prev-${vehicle.id}`}><ArrowLeft size={15} /></button>
            <span aria-live="polite">{activeImageIndex + 1} / {images.length}</span>
            <button type="button" aria-label="Nächste Fahrzeugansicht" onClick={(event) => { event.stopPropagation(); showImage(activeImageIndex + 1); }} data-testid={`vehicle-image-next-${vehicle.id}`}><ArrowRight size={15} /></button>
          </div>
          <div className="vehicle-image-dots" aria-hidden="true">{images.map((image, index) => <i className={index === activeImageIndex ? "is-active" : ""} key={`${image}-${index}`} />)}</div>
        </>}
        <button className={`vehicle-save ${isFavorite ? "is-saved" : ""}`} type="button" aria-pressed={isFavorite} aria-label={isFavorite ? "Aus Merkliste entfernen" : "Auf Merkliste setzen"} onClick={(event) => { event.stopPropagation(); onToggleFavorite(vehicle.id); }} data-testid={`vehicle-favorite-${vehicle.id}`}><Heart size={17} strokeWidth={1.5} fill={isFavorite ? "currentColor" : "none"} /></button>
      </div>
      <div className="vehicle-content">
        <div className="vehicle-heading"><div><span className="mono-label">{vehicle.brand} · {vehicle.year}</span><h3>{vehicle.title}</h3><p>{vehicle.edition}</p></div><strong className="vehicle-price">{vehicle.price}</strong></div>
        <div className="vehicle-specs"><span>{vehicle.power}</span><span>{vehicle.mileage}</span><span>{vehicle.fuel}</span><span>{vehicle.gearbox}</span></div>
        <div className="vehicle-footer"><span className="vehicle-monthly"><strong>{vehicle.availability}</strong>{vehicle.monthly}</span><div className="vehicle-actions"><button className={`vehicle-compare ${isCompared ? "is-selected" : ""}`} type="button" aria-pressed={isCompared} disabled={compareDisabled && !isCompared} onClick={() => onToggleCompare(vehicle.id)} data-testid={`vehicle-compare-${vehicle.id}`}><Scale size={14} /><span>{isCompared ? "Ausgewählt" : "Vergleichen"}</span></button><button className="vehicle-preview" type="button" onClick={() => onPreview(vehicle)} data-testid={`vehicle-preview-${vehicle.id}`}><Eye size={14} /><span>Schnellansicht</span></button><button className="text-action" type="button" onClick={() => onDetails(vehicle)} data-testid={`vehicle-details-${vehicle.id}`}><span>Details ansehen</span><ArrowUpRight size={16} strokeWidth={1.5} /></button></div></div>
      </div>
    </article>
  );
}
